import { useSubscription } from '@apollo/client';
import {
  Button,
  Dropdown,
  ExportButton,
  Icons,
  List,
  Menu,
  notification,
  Popconfirm,
  Radio,
  Select,
  ShowButton,
  Space,
  Table,
  TableProps,
  Tag,
} from '@pankod/refine-antd';
import { Grid } from 'antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { OrderShowDrawer } from 'components/restaurant-sales';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import { EStatus } from 'enums';
import { useUserData } from 'hooks';
import { IQueryOptionals, ISale } from 'interfaces';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAllCompanyConfig, getCompanyInfo } from 'services/config';
import { SearchIndex } from 'services/meilisearch';
import { EOrderType } from 'services/pos';
import {
  deleteRestaurantSale,
  getSales,
  getSalesAndProductStatusCount,
  getSalesDropdown,
  SalesAndProductStatusCountResponse,
  updateRestaurantPOSSaleStatus,
} from 'services/sale';
import { MESSAGE_ADDED_SUBSCRIPTION } from 'services/subscription';
import MetaData from 'utilities/metadata';
import { getWarehousesDropdown } from 'services/warehouse';
import { downloadCSVFile } from 'utilities/file';
import {
  capitalize,
  formatToLocalDateTime,
  getDateRangeOfLastXDays,
  OrderTypeMapping,
} from 'utilities/shared';
import styles from './style.module.css';

type StatusType = 'all' | 'new' | 'processing' | 'completed';
type IQuery = IQueryOptionals & { statuses?: EStatus[]; warehouse?: string };
type SaleWithTrackingStatusType = ISale & { trackingStatus: string };

export const STATUS_MAPPING = {
  [EStatus.CONFIRMED]: 'New',
  [EStatus.IN_PROGRESS]: 'Processing',
  [EStatus.COMPLETED]: 'Completed',
};

export const STATUS_MAPPING_REVERSE = Object.entries(STATUS_MAPPING).reduce((acc, [key, value]) => {
  acc[value] = key as EStatus;
  return acc;
}, {} as Record<string, EStatus>);

export const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
    remainingSeconds
  ).padStart(2, '0')}`;
};

export const calculateWaitingTime = (sale: { salesDate: Date }) => {
  const createdTime = new Date(sale.salesDate).getTime();
  const currentTime = Date.now();
  return Math.floor((currentTime - createdTime) / 1000); // Current elapsed time in seconds
};

export const getTrackingStatus = (sale: ISale) => {
  const waitingTime = calculateWaitingTime(sale);
  let trackingStatus = 'New';

  if (sale.salesStatus === EStatus.CONFIRMED && waitingTime <= WAITING_TIME_THRESHOLD) {
    trackingStatus = 'New';
  } else if (sale.salesStatus === EStatus.CONFIRMED && waitingTime > WAITING_TIME_THRESHOLD) {
    trackingStatus = 'Pending';
  } else if (sale.salesStatus === EStatus.IN_PROGRESS) {
    trackingStatus = 'Processing';
  } else if (sale.salesStatus === EStatus.COMPLETED) {
    trackingStatus = 'Completed';
  }

  return trackingStatus;
};

export const WAITING_TIME_THRESHOLD = 180; // in seconds

export const getTrackingStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'new':
      return '#e6f7ff';
    case 'pending':
      return '#fff1f0';
    case 'processing':
      return '#f6ffed';
    case 'completed':
      return '#f5f5f5';
    default:
      return '#fff';
  }
};

export const getTagColor = (trackingStatus: string): string => {
  switch (trackingStatus.toLowerCase()) {
    case 'new':
      return 'blue';
    case 'pending':
      return 'magenta';
    case 'processing':
      return 'green';
    case 'completed':
      return 'gray';
    default:
      return 'default';
  }
};

export const RestaurantSalesList: React.FC<IResourceComponentsProps> = () => {
  const breakpoint = Grid.useBreakpoint();
  const isNarrowScreen = !breakpoint.lg;
  const [sales, setSales] = useState<SaleWithTrackingStatusType[]>([]);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXDays(0);
    return {
      startDate: formatToLocalDateTime(startDate, 0, 0), // 12:00 AM
      endDate: formatToLocalDateTime(endDate, 23, 59), // 11:59 PM
    };
  });
  const [status, setStatus] = useState<StatusType>('all');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>();
  const { push } = useNavigation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();

  // Order Show Drawer
  const [openShowDrawer, setOpenShowDrawer] = useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<string>();
  const isInitialMount = useRef(true);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const { data: newSubscriptionData } = useSubscription(MESSAGE_ADDED_SUBSCRIPTION);
  const [statusCount, setStatusCount] = useState<SalesAndProductStatusCountResponse | null>(null);
  const userData = useUserData();

  const { data: canCreateRestaurantPos } = useCan({
    resource: 'restaurant-pos',
    action: 'list',
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setSales((prevSales) =>
        prevSales.map((sale) => {
          const trackingStatus = getTrackingStatus(sale);
          return { ...sale, trackingStatus };
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const query = useMemo<IQuery>(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      warehouse: selectedWarehouse === 'all' ? undefined : selectedWarehouse,
      statuses: status === 'all' ? undefined : [STATUS_MAPPING_REVERSE[capitalize(status)]],
      start: page,
      limit,
    };
  }, [dateRange.startDate, dateRange.endDate, searchText, selectedWarehouse, status, page, limit]);

  const fetchSales = useCallback(async (query: IQuery) => {
    setIsLoading(true);
    try {
      const response = await getSales(query);
      const salesWithTrackingStatus = response.data.map((sale) => {
        const trackingStatus = getTrackingStatus(sale);
        return { ...sale, trackingStatus };
      });
      setSales(salesWithTrackingStatus);
      setTotalSales(response.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to fetch sales',
      });
    }
    setIsLoading(false);
  }, []);

  const fetchStatusCount = useCallback(async () => {
    try {
      const response = await getSalesAndProductStatusCount({
        startDate: query.startDate,
        endDate: query.endDate,
        warehouse: query.warehouse,
      });
      setStatusCount(response);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to fetch status count',
      });
    }
  }, [query.endDate, query.startDate, query.warehouse]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllCompanyConfig();
        const configId = data[0].id;
        const companyInfo = await getCompanyInfo(configId);
        setAvatar(companyInfo.companyConfigPhoto[0]?.avatar?.url);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to load company logo',
        });
      }
    })();
  }, []);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        setIsLoading(true);
        const warehouses = await getWarehousesDropdown();
        const warehouseOptions = [{ id: 'all', name: 'All' }].concat(
          warehouses.map((w) => ({ id: w.id, name: w.name }))
        );
        setWarehouses(warehouseOptions);
        setSelectedWarehouse(warehouseOptions[0]?.id ?? undefined);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to fetch warehouses',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarehouses();
  }, []);

  useEffect(() => {
    fetchStatusCount();
  }, [fetchStatusCount]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchSales(query);
  }, [fetchSales, query]);

  useEffect(() => {
    if (newSubscriptionData) {
      setSubscriptionData(newSubscriptionData); // Update state when new data arrives
    }
  }, [newSubscriptionData]);

  // Update the restaurant sales list when new data is received
  useEffect(() => {
    if (subscriptionData && subscriptionData.messageAdded) {
      let parsedData: { instanceId: string; message: string } = {
        instanceId: '',
        message: '',
      };
      try {
        parsedData = JSON.parse(subscriptionData.messageAdded.content);
      } catch (error) {
        console.error('Error parsing message content:', error);
      }

      // Check if the instanceId matches the current platform ID
      if (!parsedData.instanceId || userData?.user?.platform?.id !== parsedData.instanceId) {
        return;
      }

      notification.success({
        message: 'Message',
        description: `${parsedData.message}`,
      });
      fetchSales(query); // Fetch latest sales
      fetchStatusCount();

      // Reset subscriptionData to null after processing
      setSubscriptionData(null); // Prevent re-triggering
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData, query]);

  const getButtonStyle = (value: StatusType, bgColor: string) => ({
    backgroundColor: status === value ? undefined : bgColor, // Keep default Ant Design primary when selected
  });

  const getCountStyle = (value: StatusType) => ({
    fontSize: 12,
    border: '1px solid #999',
    borderRadius: 100,
    padding: '0 4px',
    color: status === value ? '#fff' : '#000',
    transition: 'all 0.3s ease',
  });

  const deleteConfirmHandler = async (id: string) => {
    try {
      await deleteRestaurantSale(id);
      await fetchSales({
        startDate: query.startDate,
        endDate: query.endDate,
      });
      await fetchStatusCount();
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a sale',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete sale',
      });
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const salesData = await getSalesDropdown(query.startDate, query.endDate);
      const rows = [['Order ID', 'Date', 'Status', 'Client Name', 'Phone', 'Amount']];
      salesData.forEach((element) => {
        const clientName = element.client.name;
        const clientPhoneNo = element.client.mobileNumber;
        rows.push([
          element.id,
          element.salesDate.toString(),
          element.salesStatus,
          clientName,
          clientPhoneNo,
          element.totalAmount.toString(),
        ]);
      });

      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExporting(false);
  };

  const handleStatusUpdate = async (id: string, status: EStatus) => {
    try {
      await updateRestaurantPOSSaleStatus(id, status);
      await fetchSales(query);
      await fetchStatusCount();
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (_) {
      notification.error({
        message: 'Failed',
        description: 'Failed to change status',
      });
    }
  };

  const pageHeader = (
    <Space>
      {canCreateRestaurantPos?.can && (
        <Button
          icon={<Icons.PlusSquareOutlined />}
          type="primary"
          onClick={() => push('/restaurant-pos')}
        >
          Create
        </Button>
      )}

      <ExportButton loading={isExporting} onClick={handleExport}></ExportButton>

      <Space style={{ marginLeft: '2rem' }}>
        <img
          src={avatar || defaultAvatar}
          style={{ objectFit: 'contain' }}
          alt={'test'}
          height={32}
          width={71}
        />
      </Space>
    </Space>
  );

  const onTableChange: TableProps<SaleWithTrackingStatusType>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange((prev) => {
      const startDate = values.startDate || prev.startDate;
      const endDate = values.endDate || prev.endDate;

      return {
        startDate: startDate ? formatToLocalDateTime(new Date(startDate), 0, 0) : undefined, // 12:00 AM
        endDate: endDate ? formatToLocalDateTime(new Date(endDate), 23, 59) : undefined, // 11:59 PM
      };
    });
    setSearchText(values.searchString);
    setPage(1);
  };

  const showOrderShowDrawer = (saleId: string) => {
    setSelectedOrderId(saleId);
    setOpenShowDrawer(true);
  };

  return (
    <>
      <MetaData />
      <OrderShowDrawer
        open={openShowDrawer}
        orderId={selectedOrderId}
        onClose={() => setOpenShowDrawer(false)}
        width={500}
      />

      <List headerProps={{ extra: pageHeader }}>
        <SearchWithDateRange
          placeholder="Search by Order ID, Client Name"
          size="large"
          className={styles['filter-section__search']}
          index={SearchIndex.Sales}
          groups={['id', 'client']}
          onSearch={onSearch}
          dateRange={getDateRangeOfLastXDays(0)}
        />

        <div
          style={{
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: isNarrowScreen ? 'column' : 'row',
            alignItems: isNarrowScreen ? 'stretch' : 'center',
            gap: isNarrowScreen ? '0.75rem' : '1rem',
          }}
        >
          <div style={{ width: '100%', overflowX: isNarrowScreen ? 'auto' : 'visible' }}>
            <Radio.Group
              size="middle"
              buttonStyle="solid"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                display: 'inline-flex',
                flexWrap: 'nowrap',
                minWidth: 'max-content',
              }}
            >
              <Radio.Button value="all">All</Radio.Button>
              <Radio.Button
                value="new"
                style={getButtonStyle('new', getTrackingStatusColor('new'))}
              >
                New{' '}
                <span style={{ ...getCountStyle('new') }}>
                  {statusCount?.salesStatusCount.orderConfirmed ?? 0}
                </span>
              </Radio.Button>
              <Radio.Button
                value="processing"
                style={getButtonStyle('processing', getTrackingStatusColor('processing'))}
              >
                Processing{' '}
                <span style={{ ...getCountStyle('processing') }}>
                  {statusCount?.salesStatusCount.deliveryInProgress ?? 0}
                </span>
              </Radio.Button>
              <Radio.Button
                value="completed"
                style={getButtonStyle('completed', getTrackingStatusColor('completed'))}
              >
                Completed{' '}
                <span style={{ ...getCountStyle('completed') }}>
                  {statusCount?.salesStatusCount.completed ?? 0}
                </span>
              </Radio.Button>
            </Radio.Group>
          </div>

          <Select
            showSearch
            style={{
              minWidth: isNarrowScreen ? undefined : 200,
              width: isNarrowScreen ? '100%' : undefined,
            }}
            value={selectedWarehouse}
            onChange={(value) => setSelectedWarehouse(value)}
            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        <Table
          bordered
          rowKey="id"
          dataSource={sales}
          loading={isLoading}
          scroll={{ x: isNarrowScreen ? 1200 : 'max-content' }}
          onChange={onTableChange}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            total: totalSales,
            current: page,
            pageSize: limit,
            position: ['bottomCenter'],
          }}
          onRow={(record) => {
            const backgroundColor = getTrackingStatusColor(record.trackingStatus);
            return {
              style: { backgroundColor },
            };
          }}
        >
          <Table.Column<SaleWithTrackingStatusType> dataIndex="id" title="Order ID" />
          <Table.Column<SaleWithTrackingStatusType>
            dataIndex={['client', 'clientCode']}
            title="Member ID"
          />
          <Table.Column<ISale>
            dataIndex={['client', 'name']}
            title="Member Name"
            render={(value) => (
              <Tag onClick={() => setSearchParams({ client: value })} style={{ cursor: 'pointer' }}>
                {value}
              </Tag>
            )}
          />
          <Table.Column<SaleWithTrackingStatusType>
            dataIndex="orderType"
            title="Order Type"
            render={(value) => {
              if (!value) return null;

              return <Tag>{OrderTypeMapping[value as EOrderType]}</Tag>;
            }}
          />
          <Table.Column<SaleWithTrackingStatusType>
            dataIndex={['warehouse', 'name']}
            title="Location"
          />
          <Table.Column<ISale> dataIndex={['createdBy', 'name']} title="Staff" />
          <Table.Column<SaleWithTrackingStatusType>
            title="WAITING FOR"
            key="waitingTime"
            width={140}
            render={(_: any, record) => {
              if (record.salesStatus === EStatus.COMPLETED) {
                const createdTime = new Date(record.salesDate).getTime();
                const completedTime = new Date(record.salesCompletedAt).getTime();
                const timeDiff = Math.floor((completedTime - createdTime) / 1000);
                return <span>{formatTime(timeDiff)}</span>;
              }

              const waitingTime = calculateWaitingTime(record);
              return <span>{formatTime(waitingTime)}</span>;
            }}
          />
          <Table.Column<SaleWithTrackingStatusType>
            dataIndex="Status"
            title="Status"
            width={120}
            render={(_, record) => {
              return (
                <Tag color={getTagColor(record.trackingStatus)}>
                  {record.trackingStatus.toUpperCase()}
                </Tag>
              );
            }}
          />
          <Table.Column<SaleWithTrackingStatusType>
            title="Actions"
            fixed={isNarrowScreen ? undefined : 'right'}
            dataIndex="actions"
            width={150}
            render={(_, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton
                    size="small"
                    hideText
                    onClick={(e) => {
                      e.preventDefault();
                      showOrderShowDrawer(record.id);
                    }}
                  />
                  <Popconfirm
                    title="Are you sure?"
                    okText="Delete"
                    onConfirm={() => deleteConfirmHandler(record.id)}
                  >
                    <Button size="small" danger icon={<Icons.DeleteOutlined />} />
                  </Popconfirm>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StatusUpdateAction record={record} handleStatusUpdate={handleStatusUpdate} />
                  </div>
                </Space>
              );
            }}
          />
        </Table>
      </List>
    </>
  );
};

type StatusUpdateActionProps = {
  record: SaleWithTrackingStatusType;
  handleStatusUpdate: (id: string, status: EStatus) => Promise<void>;
};

const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({ record, handleStatusUpdate }) => {
  const moreMenu = (record: SaleWithTrackingStatusType) => (
    <Menu mode="vertical" onClick={({ domEvent }) => domEvent.stopPropagation()}>
      <Menu.Item
        key="confirmed"
        disabled={
          record.salesStatus === EStatus.CONFIRMED || record.salesStatus === EStatus.COMPLETED
        }
        onClick={() => handleStatusUpdate(record.id, EStatus.CONFIRMED)}
      >
        New
      </Menu.Item>
      <Menu.Item
        key="progress"
        disabled={
          record.salesStatus === EStatus.IN_PROGRESS || record.salesStatus === EStatus.COMPLETED
        }
        onClick={() => handleStatusUpdate(record.id, EStatus.IN_PROGRESS)}
      >
        Processing
      </Menu.Item>
      <Menu.Item
        key="completed"
        disabled={record.salesStatus === EStatus.COMPLETED}
        onClick={() => handleStatusUpdate(record.id, EStatus.COMPLETED)}
      >
        Completed
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown overlay={moreMenu(record)} trigger={['click']}>
      <Button size="small" icon={<Icons.MoreOutlined />} onClick={(e) => e.stopPropagation()} />
    </Dropdown>
  );
};
